import { useEffect } from "react";
import { useDispatch } from "react-redux";
import { Outlet, useLocation } from "react-router-dom";
import ErrorPopup from "../Components/ErrorPopup/ErrorPopup";
import Header from "../Components/Header/Header";
import SideBar from "../Components/SideBar/SideBar";
import useAccessToken from "../Custom/useAccessToken";
import { fetchAdminAccess } from "../Redux/AdminAccess/AdminAccessActions";
import { AppDispatch } from "../Redux/Store";
import { useTheme } from "../Theme/ThemeContext";

const Layout = () => {
  const location = useLocation();
  const { setDark } = useTheme();
  const dispatch = useDispatch<AppDispatch>();
  const { accessToken } = useAccessToken();

  useEffect(() => {
    if (accessToken) {
      dispatch(fetchAdminAccess(accessToken));
    }
  }, [accessToken, dispatch]);

  const isEmbeddingMode = (() => {
    const searchParams = new URLSearchParams(location.search);
    const embeddingParam = searchParams.get("embedding") || searchParams.get("embedded");

    const isInIframe = (() => {
      try {
        return window.self !== window.top;
      } catch {
        return true;
      }
    })();

    return (
      location.pathname.toLowerCase().includes("/embedding") ||
      embeddingParam === "true" ||
      embeddingParam === "1" ||
      isInIframe
    );
  })();

  // Apply theme from URL query param when embedded (e.g. ?theme=dark)
  useEffect(() => {
    if (!isEmbeddingMode) return;
    const searchParams = new URLSearchParams(location.search);
    const theme = searchParams.get("theme");
    if (theme === "dark") setDark(true);
    else if (theme === "light") setDark(false);
  }, [location.search, isEmbeddingMode, setDark]);

  // Listen for live theme updates via postMessage from parent window
  useEffect(() => {
    if (!isEmbeddingMode) return;
    const handleMessage = (event: MessageEvent) => {
      if (event.data?.type === "THINK_THEME") {
        if (event.data.theme === "dark") setDark(true);
        else if (event.data.theme === "light") setDark(false);
      }
    };
    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [isEmbeddingMode, setDark]);

  return (
    <div className={`dashboard ${isEmbeddingMode ? "embedding-mode" : ""}`}>
      {!isEmbeddingMode && <Header />}
      {!isEmbeddingMode && <SideBar />}
      <Outlet />
      <ErrorPopup />
    </div>
  );
};

export default Layout;

